import { Box, Flex, Icon } from "@chakra-ui/react";
import { BookDemoButton } from "components/event-tracking";
import { Section } from "components/section";
import { type BrandColor, type Theme, type ThemeBgColor } from "contexts/color-theme";
import React from "react";
import Balancer from "react-wrap-balancer";
import { TextTitle } from "ui/text";
import { HeroCTA } from "./cta";

type ProductPageHeroProps = {
  theme: Theme;
  variant: BrandColor;
  eyebrow?: string;
  title: string;
  description: string;
  includeSignupAction?: boolean; // eslint-disable-line react/boolean-prop-naming
  media: JSX.Element;
};

/**
 * Renders a hero intended for use primarily with product pages. Includes vertically-stacked elements
 * for the title and featured media element (e.g. `ProductFeaturesWithAnimation`).
 */
export function ProductPageHero({
  theme,
  variant,
  eyebrow,
  title,
  description,
  includeSignupAction,
  media,
}: ProductPageHeroProps) {
  const bgColor: ThemeBgColor = theme === "light" ? `new.${variant}.150` : `new.${variant}.950`;
  const spotlightColor = theme === "light" ? "white" : `new.${variant}.900`;

  return (
    // Need to use two `Section`s for easy header theming support, but want just one semantic section element
    <Box as="section" pos="relative" overflow="hidden" bg={bgColor}>
      <BackgroundEffects theme={theme} bgColor={bgColor} spotlightColor={spotlightColor} />

      <Box pos="relative">
        <Section as="div" theme={theme} themeBgColor={bgColor} my="0" pt="biggest">
          <TextTitle
            theme={theme}
            size="xl"
            align="center"
            chip={eyebrow ? { text: eyebrow } : undefined}
            titleAs="h1"
            title={<Balancer>{title}</Balancer>}
            description={description}
            actions={includeSignupAction ? <HeroCTA /> : <BookDemoButton />}
          />
        </Section>

        <Section as="div" theme={theme} themeBgColor={theme === "light" ? "white" : bgColor} my="0" py="biggest">
          {media}
        </Section>
      </Box>
    </Box>
  );
}

function BackgroundEffects({
  theme,
  bgColor,
  spotlightColor,
}: {
  theme: Theme;
  bgColor: string;
  spotlightColor: string;
}) {
  return (
    <>
      <Flex pos="absolute" left="0" right="0" bottom="0" justifyContent="center">
        {/** Mobile spotlight */}
        <Icon
          display={{ base: "inline-block", md: "none" }}
          width="768px"
          height="1180px"
          viewBox="0 0 768 1180"
          fill="none"
        >
          <g filter="url(#hero-mobile-b)" clipPath="url(#hero-mobile-a)">
            <Box
              as="path"
              d="M1391 1421c0 556.15-450.849 1007-1007 1007-556.151 0-1007-450.85-1007-1007 0-556.151 450.849-1007 1007-1007 556.151 0 1007 450.849 1007 1007Z"
              fill={spotlightColor}
            />
          </g>
          <defs>
            <clipPath id="hero-mobile-a">
              <path fill="white" d="M0 0h768v1180H0z" />
            </clipPath>
            <filter
              id="hero-mobile-b"
              x="-1023"
              y="14"
              width="2814"
              height="2814"
              filterUnits="userSpaceOnUse"
              colorInterpolationFilters="sRGB"
            >
              <feFlood floodOpacity="0" result="BackgroundImageFix" />
              <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
              <feGaussianBlur stdDeviation="200" result="effect1_foregroundBlur_334_5098" />
            </filter>
          </defs>
        </Icon>

        {/** Desktop spotlight */}
        <Icon
          display={{ base: "none", md: "inline-block" }}
          width="2900px"
          height="1422px"
          viewBox="0 0 2900 1422"
          fill="none"
        >
          <g filter="url(#hero-desktop-b)" clipPath="url(#hero-desktop-a)">
            <Box as="ellipse" cx="1450" cy="1277" rx="1081" ry="855" fill={spotlightColor} />
          </g>
          <defs>
            <clipPath id="hero-desktop-a">
              <path fill="white" d="M0 0h2900v1422H0z" />
            </clipPath>
            <filter
              id="hero-desktop-b"
              x="-31"
              y="22"
              width="2962"
              height="2510"
              filterUnits="userSpaceOnUse"
              colorInterpolationFilters="sRGB"
            >
              <feFlood floodOpacity="0" result="BackgroundImageFix" />
              <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
              <feGaussianBlur stdDeviation="200" result="effect1_foregroundBlur_334_3139" />
            </filter>
          </defs>
        </Icon>
      </Flex>

      {/** Top fade to bg color to ensure a smooth blend with the header */}
      <Box bg={bgColor} pos="absolute" left="-40px" right="-40px" top="-80px" h="160px" filter="blur(50px)" />

      {/** Bottom extra-wide fade to white for light themes, which is a bit more appealing on very-wide screens. */}
      {theme === "light" && (
        <>
          <Box
            bg={spotlightColor}
            pos="absolute"
            left="-40px"
            right="-40px"
            bottom="-50px"
            h="140px"
            filter="blur(80px)"
          />
          <Box
            bg={spotlightColor}
            pos="absolute"
            left="-40px"
            right="-40px"
            bottom="-50px"
            h="100px"
            filter="blur(30px)"
          />
        </>
      )}
    </>
  );
}
